import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { EMPTY, retry } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { PageInfo } from '../../graphql/models/gqlModels';
import { IAnnouncement } from '../../models/messaging';
import { selectLoggedInGuardianId } from '../../store/guardian/guardian.selectors';
import { CommunicationService } from './communication.service';

interface FilterState {
  types: string[];
  students: string[];
  schools: string[];
}

@Injectable({
  providedIn: 'root',
})
export class AnnouncementsService {
  private readonly communicationService = inject(CommunicationService);
  private readonly store = inject(Store);

  // State
  public announcements = signal<IAnnouncement[]>([]);
  public announcementCount = computed(() => this.announcements().length);
  public unreadAnnouncements = computed(() => this.announcements().filter((a) => !a.read));
  public unreadAnnouncementCount = computed(() => this.unreadAnnouncements().length);
  public filter = signal<string>('');
  public selectedMessage = signal<IAnnouncement | null>(null);

  public maxAnnouncementsToShow = signal<number>(20);

  private readonly guardianId = toSignal(this.store.select(selectLoggedInGuardianId), { initialValue: '' });

  public loading = signal<boolean>(false);
  public paginationLoading = signal<boolean>(false);
  public error = signal<string | null>(null);

  /** Server-side unread count — populated before the full message list loads */
  public serverUnreadCount = signal<number>(0);

  /** Page info from the last server response — drives infinite scroll */
  public pageInfo = signal<PageInfo | null>(null);

  public bodyLoading = signal<boolean>(false);
  public bodyError = signal<string | null>(null);

  private lastStudentIds = signal<string[]>([]);

  public latestAnnouncements = computed(() => {
    return this.unreadAnnouncements().slice(0, 7);
  });

  filters = signal<FilterState>({
    types: [],
    students: [],
    schools: [],
  });

  private currentRequestId = 0;

  public filterMode = signal<'all' | 'unread'>('all');

  public filteredAnnouncementsByMode = computed(() => {
    const mode = this.filterMode();
    const all = this.filteredAnnouncements();
    if (mode === 'unread') {
      return all.filter((a) => !a.read);
    }
    return all;
  });

  public filteredAnnouncements = computed(() => {
    const filter = (this.filter() || '').toLowerCase();
    const { types, students, schools } = this.filters();

    return this.announcements().filter((announcement) => {
      const content = (announcement.content || '').toLowerCase();
      const title = (announcement.title || '').toLowerCase();
      const sender = (announcement.sender || '').toLowerCase();

      const matchesFilter = content.includes(filter) || title.includes(filter) || sender.includes(filter);

      // Note: current behavior treats filters as "exclusions"
      const matchesType = !types.includes(announcement.type);
      const matchesStudent = !students.includes(announcement.avatar?.id || 'default');
      const matchesSchool = !schools.includes(announcement.sender);

      return matchesFilter && matchesType && matchesStudent && matchesSchool;
    });
  });

  // Load first page — resets the list and pageInfo
  loadAnnouncements(studentIds: string[]): void {
    if (!studentIds || studentIds.length === 0) {
      this.announcements.set([]);
      this.pageInfo.set(null);
      this.error.set(null);
      this.loading.set(false);
      return;
    }

    this.lastStudentIds.set(studentIds);
    this.error.set(null);
    this.loading.set(true);
    this.pageInfo.set(null);

    const requestId = ++this.currentRequestId;

    this.communicationService
      .getAppCommunications(studentIds, 0)
      .pipe(
        retry({ count: 3, delay: 1000 }),
        finalize(() => {
          if (requestId === this.currentRequestId) {
            this.loading.set(false);
          }
        }),
      )
      .subscribe({
        next: ({ items, pageInfo }) => {
          if (requestId !== this.currentRequestId) return;
          this.announcements.set(items ?? []);
          this.pageInfo.set(pageInfo);
        },
        error: (err) => {
          if (requestId !== this.currentRequestId) return;
          console.error('Failed to load announcements:', err);
          this.error.set("Couldn't load messages. Check your connection and try again.");
        },
      });
  }

  /** Loads the next page and appends results. Called by the inbox infinite scroll. */
  loadNextPage(): void {
    const pageInfo = this.pageInfo();
    if (!pageInfo?.hasNext || this.paginationLoading()) return;

    const nextOffset = (pageInfo.currentPage + 1) * pageInfo.pageSize;
    this.paginationLoading.set(true);

    this.communicationService
      .getAppCommunications(this.lastStudentIds(), nextOffset)
      .pipe(finalize(() => this.paginationLoading.set(false)))
      .subscribe({
        next: ({ items, pageInfo: newPageInfo }) => {
          this.pageInfo.set(newPageInfo);
          // Deduplicate against already-loaded items before appending
          const existingIds = new Set(this.announcements().map((a) => String(a.id)));
          const newItems = items.filter((a) => !existingIds.has(String(a.id)));
          this.announcements.update((prev) => [...prev, ...newItems]);
        },
        error: (err) => {
          console.error('Failed to load next page of announcements:', err);
        },
      });
  }

  retryLoad(): void {
    const ids = this.lastStudentIds();
    if (ids.length > 0) {
      this.loadAnnouncements(ids);
    }
  }

  /** Fetches the unread count from the server and stores it in serverUnreadCount. */
  loadUnreadCount(studentIds: string[]): void {
    const guardianId = this.guardianId();
    if (!guardianId || !studentIds.length) return;

    this.communicationService.getUnreadMessageCount(guardianId, studentIds).subscribe({
      next: (count) => this.serverUnreadCount.set(count),
      error: (err) => console.error('[AnnouncementsService] Failed to load unread count:', err),
    });
  }

  setSelectedMessage(routeParam: { id: string }) {
    const message = this.announcements().find((a) => a.id.toString() === routeParam.id);
    if (!message) {
      this.selectedMessage.set(null);
      return;
    }

    this.bodyLoading.set(false);
    this.bodyError.set(null);
    this.selectedMessage.set(message);
    this.updateAnnouncementReadStatus(message.id);

    // Fetch full details if not yet loaded:
    // - attachments (documents/photos) are never in the listing for any type
    // - email body is only loaded on demand
    const needsFetch = message.documents === undefined || (message.type === 'email' && message.content == null);

    if (needsFetch) {
      this.fetchCommunicationDetails(message.id.toString());
    }
  }

  private fetchCommunicationDetails(messageId: string): void {
    this.bodyLoading.set(true);
    this.bodyError.set(null);

    this.communicationService
      .getEmailBody(messageId)
      .pipe(
        retry({ count: 3, delay: 1000 }),
        catchError((err) => {
          console.error(`Failed to load communication details for ${messageId}:`, err);
          this.bodyError.set("Couldn't load this message. Check your connection and try again.");
          return EMPTY;
        }),
        finalize(() => this.bodyLoading.set(false)),
      )
      .subscribe(({ body, sizeWarning, sizeKb, documents, photos }) => {
        console.log('Yohhhhhhhhhhh ', documents, photos);
        this.announcements.update((list) =>
          list.map((a) => {
            if (a.id.toString() !== messageId) return a;
            const patch: Partial<typeof a> = { documents, photos };
            // Only overwrite content for emails — SMS body is already in the listing
            if (a.type === 'email') {
              Object.assign(patch, { content: body, sizeWarning, sizeKb });
            }
            return { ...a, ...patch };
          }),
        );
        const updated = this.announcements().find((a) => a.id.toString() === messageId);
        if (updated) this.selectedMessage.set(updated);
      });
  }

  retryBodyLoad(): void {
    const selected = this.selectedMessage();
    if (selected) {
      this.fetchCommunicationDetails(selected.id.toString());
    }
  }

  updateAnnouncementReadStatus(announcementId: number | string) {
    // Only decrement if the message was actually unread
    const wasUnread = this.announcements().find((a) => a.id === announcementId && !a.read);

    this.announcements.update((values: IAnnouncement[]) =>
      values.map((a) => (a.id === announcementId ? { ...a, read: true } : a)),
    );

    // Keep server count in sync with the local optimistic update
    if (wasUnread) {
      this.serverUnreadCount.update((n) => Math.max(0, n - 1));
    }

    // Sync to backend
    this.communicationService.updateReadStatus(announcementId.toString(), this.guardianId()).subscribe({
      next: (success) => {
        if (!success) {
          console.warn(
            `[updateAnnouncementReadStatus] Backend failed to update read status for announcement ${announcementId}`,
          );
        }
      },
      error: (err) => {
        console.error(
          `[updateAnnouncementReadStatus] Error updating read status for announcement ${announcementId}:`,
          err,
        );
        // Revert both the list flag and the server count
        this.announcements.update((values: IAnnouncement[]) =>
          values.map((a) => (a.id === announcementId ? { ...a, read: false } : a)),
        );
        if (wasUnread) {
          this.serverUnreadCount.update((n) => n + 1);
        }
      },
    });
  }

  updateFilters(partial: Partial<FilterState>): void {
    this.filters.set({ ...this.filters(), ...partial });
  }

  getFilteredStudents(): string[] {
    return this.filters().students;
  }

  getFilteredSchools(): string[] {
    return this.filters().schools;
  }

  getSmsAndEmailFilter(): { sms: boolean; email: boolean } {
    const types = this.filters().types;
    return {
      sms: !types.includes('sms'),
      email: !types.includes('email'),
    };
  }

  resetFilters(): void {
    this.filters.set({
      types: [],
      students: [],
      schools: [],
    });
  }

  markAllAsRead(): void {
    const filteredStudentIds = this.getFilteredStudents();

    const studentIds =
      filteredStudentIds && filteredStudentIds.length > 0
        ? filteredStudentIds
        : Array.from(
            new Set(
              this.announcements()
                .map((a) => a.avatar?.id)
                .filter((id): id is string => !!id),
            ),
          );

    if (!studentIds.length) return;

    this.communicationService.markAllAsRead(studentIds).subscribe({
      next: (success) => {
        if (success) {
          this.announcements.update((items) => items.map((a) => ({ ...a, read: true })));
          this.serverUnreadCount.set(0);
        } else {
          console.warn('Mark all as read failed');
        }
      },
      error: (err) => {
        console.error('Mark all as read error:', err);
      },
    });
  }
}
